import { toast } from "sonner";

const INVALID_ATTRIBUTE = "data-admin-invalid";
const INVALID_CLASSES = [
  "!border-red-500",
  "!bg-red-50",
  "!outline-none",
  "ring-2",
  "ring-red-300",
];

let nativeValidationInProgress = false;

const selectorForTestId = (testId) => `[data-testid="${testId}"]`;

const clearMarkedElement = (element) => {
  element.removeAttribute(INVALID_ATTRIBUTE);
  element.removeAttribute("aria-invalid");
  element.classList.remove(...INVALID_CLASSES);
};

const getFocusTarget = (element) => {
  if (element.matches("input, select, textarea, button")) {
    return element;
  }

  return element.querySelector(
    "input:not([type='hidden']):not([type='file']), select, textarea, button, [tabindex]",
  );
};

const getVisualTarget = (element) => {
  if (element.matches("input, select, textarea, button")) {
    return element;
  }

  if (element.dataset.validationSurface === "self") {
    return element;
  }

  return element.querySelector("[class*='border']") || element;
};

const markElement = (element) => {
  const visualTarget = getVisualTarget(element);
  const existingFocusTarget = getFocusTarget(element);
  const focusTarget = existingFocusTarget || visualTarget;
  const marked = [element, visualTarget, focusTarget].filter(Boolean);

  if (!existingFocusTarget && focusTarget instanceof HTMLElement) {
    focusTarget.tabIndex = -1;
  }

  marked.forEach((target) => {
    target.setAttribute(INVALID_ATTRIBUTE, "true");
    target.setAttribute("aria-invalid", "true");
    target.classList.add(...INVALID_CLASSES);
  });

  return focusTarget || visualTarget;
};

const focusMarkedElement = (element) => {
  if (!element) {
    return;
  }

  element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  window.setTimeout(() => {
    if (typeof element.focus === "function") {
      element.focus({ preventScroll: true });
    }
  }, 250);
};

export const clearAdminValidationErrors = () => {
  document.querySelectorAll(`[${INVALID_ATTRIBUTE}="true"]`).forEach(clearMarkedElement);
};

export const clearAdminValidationError = (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }

  let current = target;
  while (current) {
    if (current.getAttribute(INVALID_ATTRIBUTE) === "true") {
      clearMarkedElement(current);
    }
    current = current.parentElement;
  }
};

export const clearAdminValidationErrorByTestId = (testId) => {
  const element = document.querySelector(selectorForTestId(testId));
  if (!element) {
    return;
  }

  clearAdminValidationError({ target: element });
};

export const showAdminFieldErrors = (message, testIds) => {
  clearAdminValidationErrors();
  const elements = testIds
    .map((testId) => document.querySelector(selectorForTestId(testId)))
    .filter(Boolean);
  const focusTarget = elements.map(markElement)[0];

  toast.error(message);
  focusMarkedElement(focusTarget);
};

export const handleAdminNativeInvalid = (event) => {
  event.preventDefault();
  if (nativeValidationInProgress) {
    return;
  }

  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }

  nativeValidationInProgress = true;
  window.setTimeout(() => {
    nativeValidationInProgress = false;
  }, 0);
  clearAdminValidationErrors();
  const form = target.closest("form");
  const invalidFields = form
    ? Array.from(form.elements).filter((element) => element.willValidate && !element.validity.valid)
    : [target];
  const focusTarget = invalidFields.map(markElement)[0];
  const fieldLabel = invalidFields[0]?.closest("label")?.innerText?.split("\n")[0]
    || "required field";
  const multipleFields = invalidFields.length > 1;
  toast.error(
    multipleFields
      ? "Please complete all highlighted required fields."
      : `Please complete the highlighted ${fieldLabel}.`,
  );
  focusMarkedElement(focusTarget);
};